import React, { useState } from 'react';
import { X, BookOpen, Wheat, Trophy, Beaker, User, Search, Home, Stethoscope, ChefHat } from 'lucide-react';
import { StarterGuide } from './sections/StarterGuide';
import { BakingSessionGuide } from './sections/BakingSessionGuide';
import { ChallengesGuide } from './sections/ChallengesGuide';
import { AnalysisGuide } from './sections/AnalysisGuide';
import { AccountGuide } from './sections/AccountGuide';

interface HelpCenterModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTopic?: string;
}

type HelpTopic = 'overview' | 'starter' | 'baking' | 'challenges' | 'analysis' | 'account';

export function HelpCenterModal({ isOpen, onClose, initialTopic = 'overview' }: HelpCenterModalProps) {
  const [selectedTopic, setSelectedTopic] = useState<HelpTopic>(initialTopic as HelpTopic);
  const [searchQuery, setSearchQuery] = useState('');

  if (!isOpen) return null;

  const topics = [
    { id: 'overview', label: 'Overview', icon: Home },
    { id: 'starter', label: 'Creating Starters', icon: Beaker },
    { id: 'baking', label: 'Baking Sessions', icon: ChefHat },
    { id: 'challenges', label: 'Challenges', icon: Trophy },
    { id: 'analysis', label: 'Bread Analysis', icon: Stethoscope },
    { id: 'account', label: 'Account Settings', icon: User },
  ] as const;

  const renderContent = () => {
    switch (selectedTopic) {
      case 'overview':
        return (
          <div className="space-y-6">
            <h2 className="text-3xl text-white font-brand font-light italic">Welcome to Breadlify Help Center</h2>
            <p className="text-white/90 text-lg">
              Your comprehensive guide to mastering sourdough baking with Breadlify.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
              {topics.slice(1).map((topic) => {
                const Icon = topic.icon;
                return (
                  <button
                    key={topic.id}
                    onClick={() => setSelectedTopic(topic.id as HelpTopic)}
                    className="p-6 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all duration-300 text-left group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-amber-500/20 rounded-lg group-hover:bg-amber-500/30 transition-colors">
                        <Icon className="w-6 h-6 text-amber-400" />
                      </div>
                      <div>
                        <h3 className="text-lg text-white font-medium mb-2">{topic.label}</h3>
                        <p className="text-sm text-white/70">
                          {topic.id === 'starter' && 'Learn how to create and manage your sourdough starters'}
                          {topic.id === 'baking' && 'Plan and schedule your baking sessions'}
                          {topic.id === 'challenges' && 'Join community challenges and improve your skills'}
                          {topic.id === 'analysis' && 'Analyze your bread and get improvement tips'}
                          {topic.id === 'account' && 'Manage your profile, security, and preferences'}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 p-6 bg-amber-500/10 border border-amber-500/20 rounded-xl">
              <h3 className="text-lg text-white font-medium mb-2">Getting Started</h3>
              <p className="text-white/80 mb-4">
                New to Breadlify? We recommend starting with these steps:
              </p>
              <ol className="space-y-2 text-white/80">
                <li className="flex items-start gap-2">
                  <span className="text-amber-400 font-bold">1.</span>
                  <span>Create your first sourdough starter</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-400 font-bold">2.</span>
                  <span>Plan your first baking session</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-400 font-bold">3.</span>
                  <span>Follow the timeline and notifications</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-400 font-bold">4.</span>
                  <span>Analyze your results to improve</span>
                </li>
              </ol>
            </div>
          </div>
        );
      case 'starter':
        return <StarterGuide />;
      case 'baking':
        return <BakingSessionGuide />;
      case 'challenges':
        return <ChallengesGuide />;
      case 'analysis':
        return <AnalysisGuide />;
      case 'account':
        return <AccountGuide />;
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg w-full max-w-5xl max-h-[90vh] flex flex-col text-white">
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl text-white font-brand font-light italic">Help Center</h2>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/50" />
            <input
              type="text"
              placeholder="Search help topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
            />
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="w-48 border-r border-white/10 p-4 overflow-y-auto hidden md:block">
            <nav className="space-y-1">
              {topics.map((topic) => {
                const Icon = topic.icon;
                return (
                  <button
                    key={topic.id}
                    onClick={() => setSelectedTopic(topic.id as HelpTopic)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left ${
                      selectedTopic === topic.id
                        ? 'bg-white/20 text-white'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm truncate">{topic.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="flex-1 overflow-y-auto p-6 md:p-8">
            {renderContent()}
          </div>
        </div>

        <div className="md:hidden border-t border-white/10 p-4">
          <select
            value={selectedTopic}
            onChange={(e) => setSelectedTopic(e.target.value as HelpTopic)}
            className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
          >
            {topics.map((topic) => (
              <option key={topic.id} value={topic.id}>
                {topic.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
